import { STEP_LABELS } from "../data/mockData";
import { LEAD_STEP, FIN_STEP, PROPERTY_STEP } from "../data/leadFormSteps";
import Stepper from "../components/Stepper";
import DynamicFieldGrid from "../components/DynamicFieldGrid";
import DynamicAccordionBlock from "../components/DynamicAccordionBlock";
import BankGrid from "../components/BankGrid";
import { MAX_FINANCIERS } from "../hooks/useApplicationForm";
import DocumentsStep from "./DocumentsStep";
import CustomerDetailsStep from "./CustomerDetailsStep";
import { BackIcon, HomeIcon } from "../components/icons";
import Spinner from "../components/Spinner";
import CancelNote from "../components/CancelNote";

const STEP_TITLES = {
  1: "Lead Details",
  2: "Financier & Bank Selection",
  3: "Upload Docs",
  4: "Loan Details",
  5: "Customer Details",
};

const CTA_LABELS = {
  1: "Submit & Next",
  2: "Submit & Next",
  3: "Next",
  4: "Submit & Next",
  5: "Submit Application",
};

export default function ApplicationFormPage(props) {
  const {
    configOptions,
    step,
    loanId,
    activeName,
    mobile,
    savedAt,
    onBack,
    onGoHome,
    onGoStep,
    maxStepReached,
    appStatus,
    onCancelApplication,
    onNext,
    savingLead,
    savingFinancier,
    savingProperty,
    savingCustomer,
    loadingLeadDetails,
    v,
    onFieldChange,
    fieldErrors,
    openAcc,
    onToggleAcc,
    openCust,
    onToggleCust,
    fins,
    financierReadOnly,
    banksReadOnly,
    lockedBanks,
    isFrozen,
    docsLocked,
    allStepsOpen,
    financiers,
    onToggleBank,
    docs,
    docsFor,
    categories,
    autoOpen,
    onToggleAutoOpen,
    openCat,
    onToggleCat,
    over,
    drag,
    onDragStartDoc,
    onDragEndDoc,
    onDragOverReq,
    onDragLeaveReq,
    onDropReq,
    onTagDoc,
    onPreviewDoc,
    onUploadReq,
    onUntagDoc,
    onRemoveDoc,
    fileRef,
    onFiles,
    bankReqId,
    govReqId,
    capFor,
    categoriesLoading,
    showReqErrors,
    uploadErrors,
    coborrowers,
    onCoborrowerFieldChange,
    onUploadCoborrowerDoc,
    coFieldErrors,
    openCb,
    onToggleCb,
    onCopyBorrower,
  } = props;

  return (
    <div className="page-shell">
      <div className="form-header">
        <div className="container">
          <div className="form-header__row">
            <button type="button" className="form-header__icon-btn" aria-label="Back" onClick={onBack}>
              <BackIcon />
            </button>
            <div className="form-header__title">
              <h2>
                {loanId ? "Lead Details" : "Create Application"}
                {appStatus?.isLost && <span className="lost-chip">Lost</span>}
              </h2>
              <div className="form-header__sub">
                {loadingLeadDetails ? (
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                    <Spinner /> Loading saved details…
                  </span>
                ) : loanId ? (
                  activeName + " • " + loanId
                ) : (
                  activeName + " • " + mobile
                )}
              </div>
            </div>
            <div className="form-header__actions">
              {/* Cancel application — every creation step once the lead exists,
                  up to NOA Approval and while no bank has signed. */}
              {loanId && !isFrozen && !appStatus?.isLost && !appStatus?.isContractSigned && (
                <button type="button" className="cancel-app-link" onClick={onCancelApplication}>
                  Cancel application
                </button>
              )}
              <span className="saved-chip">{savedAt}</span>
              <button type="button" className="home-btn" onClick={onGoHome}>
                <HomeIcon />
                <span className="home-btn__label">Home</span>
              </button>
            </div>
          </div>
          <Stepper
            steps={STEP_LABELS}
            current={step}
            onGo={onGoStep}
            size="form"
            maxStep={allStepsOpen ? STEP_LABELS.length : maxStepReached}
          />
        </div>
      </div>

      <div style={{ flex: "1 0 auto" }}>
        <div className="container">
          <div className="sheet-card">
            {/* Lost: the application's cancel reason, date and remark on every step. */}
            {appStatus?.isLost && (
              <div className="form-cancel-note">
                <CancelNote details={appStatus.cancelDetails} wholeApp />
              </div>
            )}
            <h3 className="step-title">{STEP_TITLES[step]}</h3>

            {step === 1 && (
              <DynamicFieldGrid
                fields={LEAD_STEP.blocks[0].fields}
                values={v}
                onChange={onFieldChange}
                errors={fieldErrors}
                configOptions={configOptions}
                readOnly={!!loanId || isFrozen}
                showRequired
              />
            )}

            {step === 2 && (
              <>
                <DynamicFieldGrid
                  fields={FIN_STEP.blocks[0].fields}
                  values={v}
                  onChange={onFieldChange}
                  errors={fieldErrors}
                  configOptions={configOptions}
                  readOnly={financierReadOnly}
                  showRequired
                />
                <div style={{ marginTop: 8 }}>
                  <div style={{ font: "700 16px/24px 'Poppins'", margin: "14px 0 12px" }}>
                    Select Banks <span style={{ font: "400 12px/1 'Poppins'", color: "var(--ink-500)" }}>— choose up to {MAX_FINANCIERS}</span>
                  </div>
                  <BankGrid banks={financiers} fins={fins} onToggle={onToggleBank} disabled={banksReadOnly} locked={lockedBanks} max={MAX_FINANCIERS} />
                </div>
              </>
            )}

            {step === 3 && (
              <DocumentsStep
                docs={docs}
                docsFor={docsFor}
                categories={categories}
                autoOpen={autoOpen}
                onToggleAutoOpen={onToggleAutoOpen}
                openCat={openCat}
                onToggleCat={onToggleCat}
                over={over}
                drag={drag}
                onDragStartDoc={onDragStartDoc}
                onDragEndDoc={onDragEndDoc}
                onDragOverReq={onDragOverReq}
                onDragLeaveReq={onDragLeaveReq}
                onDropReq={onDropReq}
                onTagDoc={onTagDoc}
                onPreviewDoc={onPreviewDoc}
                onUploadReq={onUploadReq}
                onUntagDoc={onUntagDoc}
                onRemoveDoc={onRemoveDoc}
                fileRef={fileRef}
                onFiles={onFiles}
                bankReqId={bankReqId}
                govReqId={govReqId}
                capFor={capFor}
                categoriesLoading={categoriesLoading}
                showReqErrors={showReqErrors}
                uploadErrors={uploadErrors}
                readOnly={docsLocked}
              />
            )}

            {step === 4 && (
              <>
                {PROPERTY_STEP.blocks.map((block) => (
                  <DynamicAccordionBlock
                    key={block.key}
                    title={block.title}
                    open={!!openAcc[block.key]}
                    onToggle={() => onToggleAcc(block.key)}
                    fields={block.fields}
                    values={v}
                    onChange={onFieldChange}
                    errors={fieldErrors}
                    configOptions={configOptions}
                    readOnly={isFrozen}
                    showRequired
                  />
                ))}
              </>
            )}

            {step === 5 && (
              <CustomerDetailsStep
                configOptions={configOptions}
                v={v}
                onFieldChange={onFieldChange}
                coborrowers={coborrowers}
                onCoborrowerFieldChange={onCoborrowerFieldChange}
                onUploadCoborrowerDoc={onUploadCoborrowerDoc}
                openCust={openCust}
                onToggleCust={onToggleCust}
                openCb={openCb}
                onToggleCb={onToggleCb}
                onCopyBorrower={onCopyBorrower}
                errors={fieldErrors}
                coErrors={coFieldErrors}
                readOnly={isFrozen}
                showRequired
              />
            )}
          </div>
        </div>
      </div>

      <div className="footer-bar">
        <div className="container">
          <div className="footer-bar__row">
            <span className="footer-bar__saved">{savedAt}</span>
            {/* Every tab: straight back to the dashboard listing. */}
            <button type="button" className="footer-bar__back" onClick={onGoHome}>
              <BackIcon size={16} />
              Back
            </button>
            <button
              type="button"
              className="footer-bar__cta"
              onClick={onNext}
              disabled={
                (step === 1 && savingLead) || (step === 2 && savingFinancier) || (step === 4 && savingProperty) || (step === 5 && savingCustomer)
              }
            >
              {(step === 1 && savingLead) || (step === 2 && savingFinancier) || (step === 4 && savingProperty) || (step === 5 && savingCustomer) ? (
                <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                  <Spinner light /> Saving…
                </span>
              ) : isFrozen
                ? step === 5 || (!allStepsOpen && step >= maxStepReached)
                  ? "Close"
                  : "Next"
                : (step === 1 && loanId) || (step === 2 && financierReadOnly)
                ? "Next"
                : CTA_LABELS[step]}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
